import React from "react";
import { cn } from "@repo/utils/cn";
import { tv, VariantProps } from "tailwind-variants";
import { ChevronRightIcon } from "./icons/ChevronRightIcon";
import { ButtonRoundSmall } from "./ButtonRoundSmall";

const paginationSection = tv({
  base: cn(
    "uipm:flex uipm:flex-col uipm:items-center uipm:w-full",
  ),
  variants: {
    topPadding: {
      none: "uipm:pt-0",
      default: "uipm:pt-[var(--Container-Section-Top,32px)]",
    },
    bottomPadding: {
      none: "uipm:pb-0",
      default: "uipm:pb-[var(--Container-Section-Bottom,96px)]",
    },
  },
  defaultVariants: {
    topPadding: "default",
    bottomPadding: "default",
  },
});

const paginationWrapper = tv({
  base: "uipm:flex uipm:items-center uipm:justify-center uipm:gap-1",
});

const paginationEllipsis = tv({
  base: cn(
    "uipm:inline-flex uipm:items-center uipm:justify-center",
    "uipm:w-[50px] uipm:h-[50px] uipm:min-w-[50px]",
    "uipm:text-[var(--Typography-Sizes-body-medium,16px)]",
    "uipm:leading-[var(--Typography-Line-heights-body-medium,26px)]",
    "uipm:font-semibold",
    "uipm:text-[var(--Typography-Colors-text-faded-dark,#575757)]",
  ),
});

export type PaginationProps = VariantProps<typeof paginationWrapper> &
  VariantProps<typeof paginationSection> & {
    className?: string;
    wrapperClassName?: string;
    currentPage: number;
    totalPages: number;
    buildPageUrl: (page: number) => string;
    maxVisiblePages?: number;
  };

export function Pagination({
  className,
  wrapperClassName,
  currentPage,
  totalPages,
  buildPageUrl,
  maxVisiblePages = 5,
  topPadding = "default",
  bottomPadding = "default",
}: PaginationProps) {
  const getPageNumbers = () => {
    const pages: (number | "ellipsis")[] = [];

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      const leftSiblingIndex = Math.max(currentPage - 1, 2);
      const rightSiblingIndex = Math.min(currentPage + 1, totalPages - 1);

      const shouldShowLeftEllipsis = leftSiblingIndex > 2;
      const shouldShowRightEllipsis = rightSiblingIndex < totalPages - 1;

      if (!shouldShowLeftEllipsis && shouldShowRightEllipsis) {
        for (let i = 2; i < rightSiblingIndex + 1; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
      } else if (shouldShowLeftEllipsis && !shouldShowRightEllipsis) {
        pages.push("ellipsis");
        for (let i = leftSiblingIndex; i < totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push("ellipsis");
        for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
      }

      pages.push(totalPages);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();
  const hasPrevious = currentPage > 1;
  const hasNext = currentPage < totalPages;

  if (totalPages <= 1) {
    return null;
  }

  return (
    <div className={cn(paginationSection({ topPadding, bottomPadding }), wrapperClassName)}>
      <nav
        className={cn(paginationWrapper(), className)}
        role="navigation"
        aria-label="Pagination"
      >
        {hasPrevious ? (
          <ButtonRoundSmall
            href={buildPageUrl(currentPage - 1)}
            variant="default"
            aria-label="Go to previous page"
          >
            <ChevronRightIcon
              size={24}
              color="var(--color-color-accent)"
              strokeWidth={1}
              className="uipm:rotate-180"
            />
          </ButtonRoundSmall>
        ) : (
          <ButtonRoundSmall
            variant="disabled"
            disabled
            aria-disabled={true}
            aria-label="Previous page (disabled)"
          >
            <ChevronRightIcon
              size={24}
              color="var(--color-color-light)"
              strokeWidth={1}
              className="uipm:rotate-180"
            />
          </ButtonRoundSmall>
        )}

        {pageNumbers.map((page, index) => {
          if (page === "ellipsis") {
            return (
              <div key={`ellipsis-${index}`} className={paginationEllipsis()}>
                ...
              </div>
            );
          }

          const isActive = page === currentPage;

          return (
            <ButtonRoundSmall
              key={page}
              href={buildPageUrl(page)}
              variant={isActive ? "active" : "default"}
              aria-label={`Go to page ${page}`}
              aria-current={isActive ? "page" : undefined}
            >
              {page}
            </ButtonRoundSmall>
          );
        })}

        {hasNext ? (
          <ButtonRoundSmall
            href={buildPageUrl(currentPage + 1)}
            variant="default"
            aria-label="Go to next page"
          >
            <ChevronRightIcon size={24} color="var(--color-color-accent)" strokeWidth={1} />
          </ButtonRoundSmall>
        ) : (
          <ButtonRoundSmall
            variant="disabled"
            disabled
            aria-disabled={true}
            aria-label="Next page (disabled)"
          >
            <ChevronRightIcon size={24} color="var(--color-color-light)" strokeWidth={1} />
          </ButtonRoundSmall>
        )}
      </nav>
    </div>
  );
}
